import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Download, Copy, Lock, Pencil } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { getSurvey, closeSurvey, exportCsv } from '../../services/surveyService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import SurveyParticipationTab from './tab-pages/SurveyParticipationTab';
import SurveyReportTab from './report/SurveyReportTab';
import SurveyRespondentsTab from './tab-pages/SurveyRespondentsTab';
import SurveyQuestionsTab from './tab-pages/SurveyQuestionsTab';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding-top: 8px;
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 12px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SurveyTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'draft' ? '#64748b15' :
    $status === 'active' ? '#16a34a15' :
    $status === 'closed' ? '#2563eb15' :
    '#9e9e9e15'};
  color: ${({ $status }) =>
    $status === 'draft' ? '#64748b' :
    $status === 'active' ? '#16a34a' :
    $status === 'closed' ? '#2563eb' :
    '#9e9e9e'};
`;

const CompletionText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${({ $color }) => $color || '#64748b'};
  transition: all 0.15s;
  &:hover { background: #F5F7FA; }
`;

const PublicLinkCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 14px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LinkMeta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
`;

const LinkUrl = styled.div`
  background: #F5F7FA;
  padding: 6px 10px;
  border-radius: 4px;
  word-break: break-all;
  font-size: 11px;
`;

export default function SurveyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { isSuperAdmin } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [tabHeight, setTabHeight] = useState(400);
  const tabContentRef = useRef(null);

  useEffect(() => {
    const el = tabContentRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTabHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getSurvey(id)
      .then((res) => setSurvey(res.data?.data))
      .catch(() => {});
  }, [id, refreshKey]);

  const handleRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const statusLabels = useMemo(() => ({
    draft: t('survey.draft'),
    active: t('survey.active'),
    closed: t('survey.closed'),
  }), [t]);

  const tabs = useMemo(() => {
    const list = [
      { key: 'questions', label: t('survey.questions') },
    ];
    if (survey?.isPublic) {
      list.push({ key: 'respondents', label: t('survey.respondents') });
    }
    if (!survey?.isPublic) {
      list.push({ key: 'participation', label: t('survey.participation') });
    }
    list.push({ key: 'report', label: t('survey.report.tab') });
    return list;
  }, [t, survey]);

  const [activeTab, setActiveTab] = useState(null);
  useEffect(() => {
    if (!activeTab && tabs.length > 0) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  const handleClose = useCallback(async () => {
    try {
      // Dil: kapanışta otomatik açılan AI taslağının dili (spec §8.1).
      await closeSurvey(id, i18n.language);
      setSurvey((prev) => prev ? { ...prev, status: 'closed' } : prev);
    } catch {}
  }, [id, i18n.language]);

  const handleExportCsv = useCallback(async () => {
    try {
      const res = await exportCsv(id);
      // Hata (ör. 400 eşik altı) interceptor'da toast olur ve GET'te EMPTY_RESPONSE ile çözülür — dosya inmez (final inceleme I5).
      if (!(res?.data instanceof Blob)) return;
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey-${id}-results.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
  }, [id]);

  const publicUrl = survey?.publicToken
    ? `${window.location.origin}/s/${survey.publicToken}`
    : '';

  const copyLink = useCallback(() => {
    if (publicUrl) navigator.clipboard.writeText(publicUrl);
  }, [publicUrl]);

  const downloadQR = useCallback(() => {
    const svg = document.getElementById('survey-public-qr');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 1024, 1024);
      const a = document.createElement('a');
      a.download = 'survey-qr.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, []);

  const completionRate = survey && survey.participantCount > 0
    ? Math.round((survey.completedCount / survey.participantCount) * 100)
    : 0;

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('survey.title'), path: '/surveys' },
          { label: survey?.title || '...' },
        ]} />

        {survey && (
          <InfoCard>
            <TitleRow>
              <SurveyTitle>{survey.title}</SurveyTitle>
              <StatusBadge $status={survey.status}>
                {statusLabels[survey.status] || survey.status}
              </StatusBadge>
              {(survey.status === 'active' || survey.status === 'closed') && (
                <CompletionText>
                  {survey.isPublic
                    ? `${t('survey.responseCount')}: ${survey.publicResponseCount || 0}`
                    : `${t('survey.completionRate')}: ${completionRate}%`}
                </CompletionText>
              )}
            </TitleRow>
            <ActionsRow>
              {survey.status === 'draft' && (
                <Tooltip text={t('survey.edit')}>
                  <IconBtn onClick={() => history.push(`/surveys/${id}/edit`)}>
                    <Pencil size={15} />
                  </IconBtn>
                </Tooltip>
              )}
              {/* CSV yalnız kapanıştan sonra (final inceleme C1 — canlı dosya farkı tek yanıtı açardı). */}
              {survey.status === 'closed' && (
                <Tooltip text={t('survey.exportCsv')}>
                  <IconBtn onClick={handleExportCsv}>
                    <Download size={15} />
                  </IconBtn>
                </Tooltip>
              )}
              {survey.status === 'active' && (
                <Tooltip text={t('survey.close')}>
                  <IconBtn $color="#dc2626" onClick={handleClose}>
                    <Lock size={15} />
                  </IconBtn>
                </Tooltip>
              )}
            </ActionsRow>
          </InfoCard>
        )}

        {survey && survey.isPublic && survey.publicToken && (
          <PublicLinkCard>
            <QRCodeSVG id="survey-public-qr" value={publicUrl} size={80} />
            <LinkMeta>
              <LinkUrl>{publicUrl}</LinkUrl>
              <span>
                {t('survey.responseCount')}: {survey.publicResponseCount || 0}
                {survey.maxResponses > 0 ? ` / ${survey.maxResponses}` : ''}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <Tooltip text={t('survey.copyLink')}>
                  <IconBtn onClick={copyLink} style={{ width: 28, height: 28 }}>
                    <Copy size={13} />
                  </IconBtn>
                </Tooltip>
                <Tooltip text={t('survey.downloadQr')}>
                  <IconBtn onClick={downloadQR} style={{ width: 28, height: 28 }}>
                    <Download size={13} />
                  </IconBtn>
                </Tooltip>
              </div>
            </LinkMeta>
          </PublicLinkCard>
        )}

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent ref={tabContentRef}>
        {activeTab === 'questions' && <SurveyQuestionsTab surveyId={id} questions={survey?.questions || []} isDraft={survey?.status === 'draft'} onRefresh={handleRefresh} />}
        {activeTab === 'participation' && <SurveyParticipationTab surveyId={id} surveyStatus={survey?.status} containerHeight={tabHeight} />}
        {activeTab === 'respondents' && <SurveyRespondentsTab surveyId={id} containerHeight={tabHeight} />}
        {activeTab === 'report' && survey && <SurveyReportTab survey={survey} />}
      </TabContent>
    </PageContainer>
  );
}
